import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Loader2,
  Lock,
  Mail,
  RefreshCw,
  Server,
  ShieldCheck,
  Smartphone,
  Tag,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';
import { useBrand } from '../../brand/BrandProvider';
import { Stepper } from '../../components/Stepper';
import { OptionCard } from '../../components/OptionCard';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { cn } from '../../lib/cn';
import { usePricingPagePackages } from '../../api/catalog';
import type { GatewaySlug } from '../../mocks/clientBatch';
import { gatewaysForCurrency } from '../../api/platform';
import { sendSignupOtp, verifySignupOtp, signupAccount } from '../../api/auth';
import { startCheckout } from '../../api/billing';
import { useSignupAvailability } from '../../api/hooks';
import { useSession } from '../../stores/session';
import { USE_MOCKS } from '../../api/client';

const STEPS = ['Account', 'Verify', 'Plan', 'Subdomain & DB', 'Payment'];

// Possignin bills in BDT (৳). SSLCommerz is BDT-only; international currencies +
// per-currency pricing aren't live yet, so signup is BDT-only until they land.
const CURRENCY = 'BDT';

type PayMethod = 'sslcommerz' | 'cash' | 'stripe' | 'paypal';

/** Map our PayMethod radio identifiers to the GatewaySlug values from clientBatch. */
const METHOD_TO_GATEWAY: Record<PayMethod, GatewaySlug> = {
  sslcommerz: 'sslcommerz',
  cash: 'manual_cash',
  stripe: 'stripe',
  paypal: 'paypal',
};
const GATEWAY_TO_METHOD: Record<GatewaySlug, PayMethod> = {
  sslcommerz: 'sslcommerz',
  manual_cash: 'cash',
  stripe: 'stripe',
  paypal: 'paypal',
};

/**
 * Self-signup wizard — §5.5
 * Five steps: Account, Verify, Plan, Subdomain & DB, Payment.
 * Submission redirects to /signup/provisioning.
 */
export function Signup() {
  const brand = useBrand();
  const navigate = useNavigate();
  const { data: pricingPagePackages = [] } = usePricingPagePackages();
  const { data: signupAvail } = useSignupAvailability();
  const [step, setStep] = useState(0);

  // step 0 — Account
  const [bizName, setBizName] = useState('');
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // step 1 — Verify (OTP)
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '']);
  const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(5);
  const [otpResendIn, setOtpResendIn] = useState(60);
  const [otpStatus, setOtpStatus] =
    useState<'idle' | 'verifying' | 'verified' | 'failed' | 'locked'>('idle');
  const otpInputs = useRef<Array<HTMLInputElement | null>>([null, null, null, null]);

  // step 2 — Plan (defaults to the first available package once they load)
  const [plan, setPlan] = useState<string>('');
  const [trial, setTrial] = useState(true);

  // step 3 — Subdomain & DB
  const [subdomain, setSubdomain] = useState('');
  const [db, setDb] = useState<'shared' | 'managed'>('shared');

  // step 4 — Payment
  const [payMethod, setPayMethod] = useState<PayMethod>('sslcommerz');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);

  // Keep payMethod valid for the BDT gateways available.
  useEffect(() => {
    const allowed = gatewaysForCurrency(CURRENCY);
    if (allowed.length === 0) return;
    const currentSlug = METHOD_TO_GATEWAY[payMethod];
    if (!allowed.some((g) => g.slug === currentSlug)) {
      setPayMethod(GATEWAY_TO_METHOD[allowed[0]!.slug]);
    }
  }, [payMethod]);

  // If the brand doesn't allow the shared DB, fall back to the first allowed mode.
  useEffect(() => {
    if (db === 'shared' && !brand.allowedDbModes.includes('shared')) {
      if (brand.allowedDbModes.includes('managed')) setDb('managed');
    }
  }, [brand.allowedDbModes, db]);

  // Pre-select the first available package once the catalog loads.
  useEffect(() => {
    if (plan) return;
    const first = pricingPagePackages[0];
    if (first) setPlan(first.id);
  }, [pricingPagePackages, plan]);

  // Send the verification code the first time we land on the Verify step.
  useEffect(() => {
    if (step !== 1 || otpSent) return;
    setOtpSent(true);
    void sendSignupOtp(email, mobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // OTP resend countdown — only ticks while we're on the Verify step.
  useEffect(() => {
    if (step !== 1) return;
    if (otpResendIn <= 0) return;
    const t = setInterval(() => {
      setOtpResendIn((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [step, otpResendIn]);

  // OTP auto-verify when 4 digits are filled.
  useEffect(() => {
    if (step !== 1) return;
    if (otpStatus === 'locked' || otpStatus === 'verified') return;
    const code = otpDigits.join('');
    if (code.length !== 4) return;
    if (!/^\d{4}$/.test(code)) return;
    setOtpStatus('verifying');
    const t = setTimeout(() => {
      verifySignupOtp(email, code).then((ok) => {
        if (ok) {
          setOtpStatus('verified');
          // small pause so the user sees the success state, then advance
          setTimeout(() => setStep(2), 350);
        } else {
          const newAttempts = otpAttemptsLeft - 1;
          setOtpAttemptsLeft(newAttempts);
          setOtpDigits(['', '', '', '']);
          if (newAttempts <= 0) {
            setOtpStatus('locked');
          } else {
            setOtpStatus('failed');
            // refocus first box for retry
            setTimeout(() => otpInputs.current[0]?.focus(), 0);
          }
        }
      });
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpDigits, step]);

  function setOtpDigit(i: number, raw: string) {
    // Sanitize: keep only digits; take last char if multiple
    const digit = raw.replace(/\D/g, '').slice(-1);
    setOtpDigits((prev) => {
      const copy = [...prev];
      copy[i] = digit;
      return copy;
    });
    if (digit && i < 3) {
      otpInputs.current[i + 1]?.focus();
    }
    // Clear failure flag as user starts typing again
    if (otpStatus === 'failed') setOtpStatus('idle');
  }

  function onOtpKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otpDigits[i] && i > 0) {
      otpInputs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      otpInputs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && i < 3) {
      otpInputs.current[i + 1]?.focus();
    }
  }

  function onOtpPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (text.length === 0) return;
    e.preventDefault();
    const next = ['', '', '', ''];
    for (let i = 0; i < text.length && i < 4; i++) {
      next[i] = text[i]!;
    }
    setOtpDigits(next);
    const focusIndex = Math.min(text.length, 3);
    otpInputs.current[focusIndex]?.focus();
    if (otpStatus === 'failed') setOtpStatus('idle');
  }

  function resendOtp() {
    if (otpResendIn > 0) return;
    void sendSignupOtp(email, mobile);
    setOtpDigits(['', '', '', '']);
    setOtpStatus('idle');
    setOtpResendIn(60);
    setTimeout(() => otpInputs.current[0]?.focus(), 0);
  }

  function requestNewOtpAfterLock() {
    setOtpAttemptsLeft(5);
    setOtpDigits(['', '', '', '']);
    setOtpResendIn(60);
    setOtpStatus('idle');
    setTimeout(() => otpInputs.current[0]?.focus(), 0);
  }

  function next() {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
  }
  function back() {
    if (step > 0) setStep((s) => s - 1);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (step < STEPS.length - 1) {
      next();
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await signupAccount({
        email, mobile, password, fullName, username: username.trim() || undefined, businessName: bizName, subdomain,
        packageId: selectedPackage?.id ?? '', interval: selectedInterval, currencyCode: CURRENCY,
        dbMode: db, startTrial: trial,
      });
      // Auto-login: the API set the refresh cookie; keep the access token in memory.
      const s = useSession.getState();
      s.setTokens(res.accessToken, null);
      s.setUser({ id: res.user.id, name: res.user.name, email: res.user.email });
      s.setActiveRole('owner');
      s.setActiveBusiness(res.business.id);
      // Pay-now (no trial): hand off to the SSLCommerz hosted checkout.
      if (!res.startTrial) {
        const checkout = await startCheckout({ businessId: res.business.id, priceId: res.priceId });
        if (checkout.gatewayPageURL) {
          window.location.href = checkout.gatewayPageURL;
          return;
        }
      }
      navigate({ to: '/signup/provisioning' });
    } catch (err) {
      setSubmitting(false);
      setSubmitError(err instanceof Error ? err.message : 'Could not create your account. Please try again.');
    }
  }

  const selectedPackage =
    pricingPagePackages.find((p) => p.id === plan) ?? pricingPagePackages[0];
  // Bill on whichever interval the plan actually has (monthly preferred). Reseller
  // catalogs are often yearly-only, so a hard 'month' assumption would 0-price them.
  const selectedInterval: 'month' | 'year' = selectedPackage?.priceMonthlyMinor != null ? 'month' : 'year';
  const intervalWord = selectedInterval === 'month' ? 'monthly' : 'yearly';
  const planPriceMinor = (selectedInterval === 'month' ? selectedPackage?.priceMonthlyMinor : selectedPackage?.priceYearlyMinor) ?? 0;
  const totalToday = trial ? 0 : planPriceMinor;

  /** First charge date if they take the 7-day trial (computed, not a baked literal). */
  const trialFirstChargeLabel = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(
    undefined,
    { day: 'numeric', month: 'short', year: 'numeric' },
  );

  /**
   * Continue gating:
   *  - Account (step 0): all fields + valid email + Terms.
   *  - Verify (step 1): require otpStatus === 'verified'.
   */
  const canContinue = (() => {
    if (step === 0) {
      const emailOk = /\S+@\S+\.\S+/.test(email.trim());
      // Optional, but if given it must match the one username contract the API enforces
      // (validateUsername) — catch it here rather than after the OTP is spent.
      const usernameOk = !username.trim() || /^[a-z0-9][a-z0-9._-]{2,39}$/i.test(username.trim());
      if (!bizName.trim() || !fullName.trim() || !emailOk || !usernameOk || !mobile.trim() || password.length < 8 || !acceptedTerms) return false;
    }
    if (step === 1 && otpStatus !== 'verified') return false;
    return true;
  })();

  /** Hide the Continue button on Verify once the user has filled all 4 digits — auto-advance handles it. */
  const otpAllFilled = otpDigits.every((d) => d.length === 1);
  const hideContinueButton = step === 1 && (otpAllFilled || otpStatus === 'verifying' || otpStatus === 'verified');

  // Gateways available for BDT (SSLCommerz + pay-cash).
  const availableGateways = gatewaysForCurrency(CURRENCY);

  if (signupAvail && !signupAvail.directSignup) {
    return (
      <div className="min-h-screen bg-marketing dark:bg-marketing-dark grid place-items-center px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <h1 className="text-xl font-semibold text-zinc-950 dark:text-zinc-50">Sign-ups are closed</h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            New sign-ups aren't being accepted right now. Please check back later or contact us.
          </p>
          <button
            type="button"
            onClick={() => navigate({ to: '/login' })}
            className="mt-5 text-sm font-medium text-brand dark:text-brand-dm hover:underline"
          >
            Back to sign in
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark">
      <header className="px-4 sm:px-8 py-5 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {brand.name}
          </span>
        </Link>
        <div className="text-xs text-zinc-500 dark:text-zinc-400">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-medium text-brand dark:text-brand-dm hover:underline"
          >
            Log in
          </Link>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-8 sm:py-12 max-w-3xl mx-auto">
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Start your {brand.name} trial
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            7 days free — no card needed. Cancel any time.
          </p>
        </div>

        <div className="mb-8 flex justify-center">
          <Stepper steps={STEPS} current={step} />
        </div>

        <Card className="p-5 sm:p-8">
          <form onSubmit={onSubmit} className="space-y-6">
            {step === 0 && (
              <div className="space-y-5">
                <Input
                  label="Business name"
                  required
                  value={bizName}
                  onChange={(e) => setBizName(e.target.value)}
                  placeholder="Khan Bakery"
                />
                <Input
                  label="Your full name"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Imran Khan"
                />
                <Input
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="(optional — auto-generated if empty)"
                  hint="Your POS + portal login username."
                  autoComplete="username"
                />
                <Input
                  label="Email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@your-shop.com"
                  autoComplete="email"
                />
                <Input
                  label="Mobile (BD)"
                  required
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="+8801XXXXXXXXX"
                  autoComplete="tel"
                />
                <Input
                  label="Password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  hint="Use at least 8 characters with letters and numbers"
                  autoComplete="new-password"
                />
                <label className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  <span>
                    I accept the{' '}
                    <a
                      href="https://itcitysoft.com/terms"
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand dark:text-brand-dm hover:underline"
                    >
                      Terms
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://itcitysoft.com/privacy"
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand dark:text-brand-dm hover:underline"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-semibold text-zinc-950 dark:text-zinc-50">
                    Verify your email and phone
                  </h2>
                  <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
                    We sent a 4-digit code to{' '}
                    <span className="font-medium text-zinc-900 dark:text-zinc-100">
                      {email}
                    </span>{' '}
                    and{' '}
                    <span className="font-medium text-zinc-900 dark:text-zinc-100">
                      {mobile}
                    </span>
                    .
                  </p>
                  <div className="mt-3 flex items-center justify-center gap-3 text-xs text-zinc-500 dark:text-zinc-400">
                    <span className="inline-flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" /> Email
                    </span>
                    <span aria-hidden="true">·</span>
                    <span className="inline-flex items-center gap-1">
                      <Smartphone className="w-3.5 h-3.5" /> SMS
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  {otpDigits.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => {
                        otpInputs.current[i] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={d}
                      onChange={(e) => setOtpDigit(i, e.target.value)}
                      onKeyDown={(e) => onOtpKeyDown(i, e)}
                      onPaste={onOtpPaste}
                      disabled={otpStatus === 'verifying' || otpStatus === 'verified' || otpStatus === 'locked'}
                      aria-label={`OTP digit ${i + 1}`}
                      className={cn(
                        'w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-semibold tabular-nums',
                        'bg-white dark:bg-zinc-900',
                        'border-2 rounded-lg',
                        'text-zinc-950 dark:text-zinc-50',
                        'focus:outline-none focus:ring-2',
                        otpStatus === 'failed'
                          ? 'border-rose-400 dark:border-rose-700 focus:ring-rose-500'
                          : otpStatus === 'verified'
                          ? 'border-emerald-400 dark:border-emerald-700 focus:ring-emerald-500'
                          : 'border-zinc-300 dark:border-zinc-700 focus:ring-brand dark:focus:ring-brand-dm focus:border-brand dark:focus:border-brand-dm',
                        otpStatus === 'locked' && 'opacity-60 cursor-not-allowed',
                      )}
                    />
                  ))}
                </div>

                {USE_MOCKS && (
                  <p className="text-center text-[11px] text-zinc-500 dark:text-zinc-400">
                    (mock: use 1234)
                  </p>
                )}

                {otpStatus === 'verifying' && (
                  <div className="flex items-center justify-center gap-2 text-sm text-zinc-700 dark:text-zinc-200">
                    <Loader2 className="w-4 h-4 animate-spin text-brand dark:text-brand-dm" />
                    Verifying…
                  </div>
                )}

                {otpStatus === 'verified' && (
                  <div className="flex items-center justify-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="w-4 h-4" />
                    Verified — taking you to the next step…
                  </div>
                )}

                {otpStatus === 'failed' && (
                  <div className="flex items-center justify-center gap-2 text-sm text-rose-600 dark:text-rose-400">
                    <AlertCircle className="w-4 h-4" />
                    Code didn't match — try again. {otpAttemptsLeft}{' '}
                    {otpAttemptsLeft === 1 ? 'attempt' : 'attempts'} left.
                  </div>
                )}

                {otpStatus === 'locked' && (
                  <div className="rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-4 text-center space-y-3">
                    <div className="flex items-center justify-center gap-2 text-sm text-rose-900 dark:text-rose-100">
                      <AlertCircle className="w-4 h-4" />
                      Too many attempts. Request a new code.
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={requestNewOtpAfterLock}
                      iconLeft={<RefreshCw className="w-3.5 h-3.5" />}
                    >
                      Request new code
                    </Button>
                  </div>
                )}

                {otpStatus !== 'locked' && (
                  <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-zinc-200 dark:border-zinc-700">
                    <button
                      type="button"
                      onClick={() => setStep(0)}
                      className="text-xs font-medium text-brand dark:text-brand-dm hover:underline"
                    >
                      Wrong number?
                    </button>
                    <button
                      type="button"
                      onClick={resendOtp}
                      disabled={otpResendIn > 0}
                      className={cn(
                        'inline-flex items-center gap-1.5 text-xs font-medium',
                        otpResendIn > 0
                          ? 'text-zinc-400 dark:text-zinc-500 cursor-not-allowed'
                          : 'text-brand dark:text-brand-dm hover:underline',
                      )}
                    >
                      <RefreshCw className="w-3 h-3" />
                      {otpResendIn > 0
                        ? `Resend code in ${otpResendIn}s`
                        : 'Resend code'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                {pricingPagePackages.map((pkg) => (
                  <OptionCard
                    key={pkg.id}
                    selected={plan === pkg.id}
                    onSelect={() => setPlan(pkg.id)}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-zinc-950 dark:text-zinc-50">
                            {pkg.name}
                          </span>
                          {pkg.badge && (
                            <span className="px-1.5 py-px rounded text-[10px] font-medium bg-brand text-white dark:bg-brand-dm dark:text-zinc-950">
                              {pkg.badge}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                          {pkg.features.slice(0, 3).join(' · ')}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {pkg.priceMonthlyMinor !== null ? (
                          <>
                            <Money
                              minor={pkg.priceMonthlyMinor}
                              hideCents
                              tone="neutral"
                              className="font-semibold text-zinc-950 dark:text-zinc-50"
                            />
                            <div className="text-xs text-zinc-500 dark:text-zinc-400">
                              / month
                            </div>
                          </>
                        ) : pkg.priceYearlyMinor !== null ? (
                          <>
                            <Money
                              minor={pkg.priceYearlyMinor}
                              hideCents
                              tone="neutral"
                              className="font-semibold text-zinc-950 dark:text-zinc-50"
                            />
                            <div className="text-xs text-zinc-500 dark:text-zinc-400">
                              / year
                            </div>
                          </>
                        ) : (
                          <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50">
                            Custom
                          </span>
                        )}
                      </div>
                    </div>
                  </OptionCard>
                ))}

                <label className="flex items-center gap-2.5 mt-4 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-200 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
                  <input
                    type="checkbox"
                    checked={trial}
                    onChange={(e) => setTrial(e.target.checked)}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  Start with 7-day free trial (no card needed)
                </label>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                    Subdomain
                  </label>
                  <div className="flex items-stretch rounded-md border border-zinc-300 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900 focus-within:ring-2 focus-within:ring-brand dark:focus-within:ring-brand-dm">
                    <input
                      type="text"
                      value={subdomain}
                      onChange={(e) => setSubdomain(e.target.value.replace(/[^a-z0-9-]/g, ''))}
                      className="flex-1 px-3 py-2.5 text-sm bg-transparent text-zinc-950 dark:text-zinc-50 placeholder-zinc-400 focus:outline-none mono"
                      placeholder="khan-bakery"
                    />
                    <span className="px-3 py-2.5 text-sm bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-l border-zinc-200 dark:border-zinc-700 mono whitespace-nowrap">
                      .{brand.domain}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                    Lowercase letters, numbers, and hyphens. We'll confirm availability when we set up your POS.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                    Database
                  </label>
                  <div className="space-y-3">
                    {brand.allowedDbModes.includes('shared') && (
                      <OptionCard selected={db === 'shared'} onSelect={() => setDb('shared')}>
                        <div className="flex items-start gap-3">
                          <Server className="w-5 h-5 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-zinc-950 dark:text-zinc-50">Shared (recommended)</span>
                              <span className="px-1.5 py-px rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                Default
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                              We host it on our shared platform. Fastest to start; nothing to manage.
                            </p>
                          </div>
                        </div>
                      </OptionCard>
                    )}
                    {brand.allowedDbModes.includes('managed') && (
                      <OptionCard selected={db === 'managed'} onSelect={() => setDb('managed')}>
                        <div className="flex items-start gap-3">
                          <Server className="w-5 h-5 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="font-medium text-zinc-950 dark:text-zinc-50">Isolated (your own database)</span>
                            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                              A dedicated database on our server, just for you — with phpMyAdmin access.
                            </p>
                          </div>
                        </div>
                      </OptionCard>
                    )}
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6">
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800/40 p-4 sm:p-5">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-medium mb-2">
                    Plan
                  </div>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="font-medium text-zinc-950 dark:text-zinc-50">
                        {selectedPackage?.name} ({intervalWord})
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400">
                        {trial
                          ? `7-day trial · first charge on ${trialFirstChargeLabel}`
                          : `${intervalWord.charAt(0).toUpperCase()}${intervalWord.slice(1)} subscription`}
                      </div>
                    </div>
                    <Money
                      minor={planPriceMinor}
                      hideCents
                      tone="neutral"
                      className="font-semibold text-zinc-950 dark:text-zinc-50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                    Pay with
                  </label>
                  <div className="space-y-3">
                    {availableGateways.map((gw) => {
                      const method = GATEWAY_TO_METHOD[gw.slug];
                      const isPrimary = gw.isPrimaryFor?.includes(CURRENCY) ?? false;
                      let Icon = CreditCard;
                      let title = gw.displayName;
                      let body = gw.description;
                      if (gw.slug === 'sslcommerz') {
                        Icon = CreditCard;
                        title = 'Card / bKash / Nagad / bank';
                        body = 'Secured by SSLCommerz. BDT only.';
                      } else if (gw.slug === 'manual_cash') {
                        Icon = Tag;
                        title = 'Pay cash to office';
                        body = "You'll get the bank/office details by email.";
                      }
                      return (
                        <OptionCard
                          key={gw.slug}
                          selected={payMethod === method}
                          onSelect={() => setPayMethod(method)}
                        >
                          <div className="flex items-start gap-3">
                            <Icon
                              className={cn(
                                'w-5 h-5 mt-0.5 flex-shrink-0',
                                payMethod === method
                                  ? 'text-brand dark:text-brand-dm'
                                  : 'text-zinc-500 dark:text-zinc-400',
                              )}
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-zinc-950 dark:text-zinc-50">
                                  {title}
                                </span>
                                {isPrimary && (
                                  <span className="px-1.5 py-px rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                    Recommended
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                                {body}
                              </p>
                            </div>
                          </div>
                        </OptionCard>
                      );
                    })}
                  </div>
                </div>

                {/* Order summary */}
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-950 p-4 sm:p-5">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-medium mb-3">
                    Order summary
                  </div>
                  <dl className="text-sm space-y-2">
                    <div className="flex items-center justify-between">
                      <dt className="text-zinc-700 dark:text-zinc-200">
                        {selectedPackage?.name} plan ({intervalWord})
                      </dt>
                      <dd className="tabular-nums text-zinc-950 dark:text-zinc-50">
                        <Money minor={planPriceMinor} hideCents tone="neutral" />
                      </dd>
                    </div>
                    <div className="border-t border-zinc-200 dark:border-zinc-700 pt-2 mt-2 flex items-center justify-between font-semibold text-zinc-950 dark:text-zinc-50">
                      <dt>Total today</dt>
                      <dd className="tabular-nums">
                        <Money minor={totalToday} hideCents tone="neutral" />
                      </dd>
                    </div>
                  </dl>
                </div>

                <p className="flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Secured by SSLCommerz · BDT
                </p>
              </div>
            )}

            {submitError && step === STEPS.length - 1 && (
              <p className="mt-3 text-sm text-rose-600 dark:text-rose-400" role="alert">{submitError}</p>
            )}

            {/* Footer */}
            <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
              {step > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={back}
                  iconLeft={<ArrowLeft className="w-4 h-4" />}
                >
                  Back
                </Button>
              ) : (
                <span />
              )}

              {!hideContinueButton && (
                <Button
                  type="submit"
                  loading={submitting}
                  disabled={!canContinue}
                  iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
                >
                  {step < STEPS.length - 1
                    ? 'Continue'
                    : trial
                    ? 'Start trial'
                    : 'Create account'}
                </Button>
              )}
            </div>
          </form>
        </Card>

        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <Lock className="w-3 h-3" />
          Encrypted · 7-day free trial · Cancel any time
        </p>
      </main>
    </div>
  );
}
